'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Loader2, MessageCircleQuestion } from 'lucide-react';

import { AIS_RU_TELEGRAM_BOT } from '@/lib/ais-rekvizity';
import { oknoChata, pochtaPodderzhki } from '@/lib/okno-chata';
import { useAisRuDomen } from '@/components/aisearch/ais-link';
import { celChata } from '@/lib/metrika-celi';

/**
 * Кнопка поддержки: открывает чат Chatwoot.
 *
 * Скрипт подгружается по нажатию, а не вместе со страницей. Это главное
 * решение здесь: готовый сниппет Chatwoot грузит около 300 КБ и рисует
 * свой пузырь в углу на каждой странице каждому посетителю, хотя пишут в
 * поддержку единицы. Отложенная загрузка стоит человеку одного ожидания
 * в те разы, когда он действительно решил спросить.
 *
 * Отсюда же и своя кнопка в шапке вместо стандартного пузыря: пузырь
 * закрывает угол страницы и на телефоне перекрывает содержимое, а место
 * в шапке рядом с «Войти» — там, где поддержку и ищут.
 *
 * Сервер поддержки наш (`support.aisearch.ru`), поэтому баннера о cookie
 * не требуется по той же причине, что и у счётчика.
 */

/** Адрес сервера поддержки. */
const BASE_URL = 'https://support.aisearch.ru';
const SDK = `${BASE_URL}/packs/js/sdk.js`;

/**
 * Сколько ждать чат, прежде чем предложить другой способ связи.
 *
 * Замер 27.09: первая загрузка sdk.js с домашнего канала заняла 16,8 с,
 * следующие 0,8 с; из Германии 0,35 с. Одна из двух попыток в браузере
 * не открыла окно и за 20 с, и человек всё это время смотрел на волчок
 * — именно так владелец и увидел «нажал, ничего не открылось». Восемь
 * секунд: дольше человек не ждёт, а обычной загрузке их хватает с
 * запасом.
 */
const ZHDAT_CHAT_MS = 8000;

/**
 * Chatwoot кладёт себя в `window`. Объявляем ровно то, чем пользуемся:
 * полный тип SDK нам не нужен, а `any` спрятал бы опечатку в имени.
 */
interface ChatwootWindow extends Window {
  chatwootSDK?: { run: (options: { websiteToken: string; baseUrl: string }) => void };
  $chatwoot?: { toggle: (state?: 'open' | 'close') => void };
  chatwootSettings?: { hideMessageBubble?: boolean; locale?: string };
  /** Готов ли виджет: событие `chatwoot:ready` приходит один раз. */
  __aisChatGotov?: boolean;
}

/**
 * Загрузить виджет, не открывая его.
 *
 * Один тег на страницу, сколько бы кнопок ни звали: второй запуск SDK
 * строил бы второй iframe.
 */
function zagruzitVidzhet(locale: string, okno: string): void {
  const w = window as ChatwootWindow;
  if (w.$chatwoot || document.querySelector(`script[src="${SDK}"]`)) return;

  // Настройки задаются до загрузки: SDK читает их при старте.
  // Пузырь скрыт — точка входа у нас одна, кнопка в шапке.
  w.chatwootSettings = { hideMessageBubble: true, locale };
  window.addEventListener(
    'chatwoot:ready',
    () => {
      w.__aisChatGotov = true;
    },
    { once: true },
  );

  const script = document.createElement('script');
  script.src = SDK;
  script.defer = true;
  script.async = true;
  script.onload = () => {
    w.chatwootSDK?.run({ websiteToken: okno, baseUrl: BASE_URL });
  };
  script.onerror = () => {
    // Тег убирается, иначе повтор принял бы его за загружающийся и ждал
    // бы вечно.
    script.remove();
  };
  document.head.appendChild(script);
}

/**
 * Открыть чат поддержки. Общий для кнопки в шапке и кнопок на странице:
 * один скрипт, одно окно, где бы человек ни нажал.
 *
 * `sboj` — чат не открылся за `ZHDAT_CHAT_MS`: кнопке есть что показать
 * вместо вечного волчка.
 */
export function useChatPodderzhki(): {
  open: () => void;
  prigotovit: () => void;
  loading: boolean;
  sboj: boolean;
} {
  // Язык страницы, а не браузера: человек читает витрину на выбранном
  // языке, и оператору отвечать ему на нём же.
  const locale = useLocale();
  const okno = oknoChata(locale, useAisRuDomen());

  const [loading, setLoading] = useState(false);
  const [sboj, setSboj] = useState(false);
  const tajmer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (tajmer.current) clearTimeout(tajmer.current);
    },
    [],
  );

  /** Начать загрузку заранее: при наведении или фокусе на кнопку. */
  const prigotovit = useCallback(() => {
    zagruzitVidzhet(locale, okno);
  }, [locale, okno]);

  const open = useCallback(() => {
    const w = window as ChatwootWindow;
    setSboj(false);
    // Цель ставится на нажатие, а не на открытие окна: если чат не
    // открылся, человек всё равно хотел написать, и это заявка (#218).
    celChata();

    // Уже готов: просто открываем. Обычный путь со второго нажатия и с
    // любой другой кнопки после первой.
    if (w.$chatwoot && w.__aisChatGotov) {
      w.$chatwoot.toggle('open');
      return;
    }

    zagruzitVidzhet(locale, okno);
    setLoading(true);

    const pokazat = () => {
      if (tajmer.current) clearTimeout(tajmer.current);
      setLoading(false);
      (window as ChatwootWindow).$chatwoot?.toggle('open');
    };

    // Готовность могла наступить между проверкой выше и этой строкой.
    if (w.__aisChatGotov) {
      pokazat();
      return;
    }
    window.addEventListener('chatwoot:ready', pokazat, { once: true });

    if (tajmer.current) clearTimeout(tajmer.current);
    tajmer.current = setTimeout(() => {
      // Чат может ещё догрузиться и открыться сам по событию выше: это
      // не помешает, человек просто увидит и окно, и запасные способы.
      setLoading(false);
      setSboj(true);
    }, ZHDAT_CHAT_MS);
  }, [locale, okno]);

  return { open, prigotovit, loading, sboj };
}

export function SupportButton() {
  const t = useTranslations('ais_layout');
  const { open, prigotovit, loading } = useChatPodderzhki();

  return (
    <button
      type="button"
      onClick={open}
      onPointerEnter={prigotovit}
      onFocus={prigotovit}
      aria-label={t('support')}
      title={t('support')}
      className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-[var(--color-border)] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)] hover:border-[var(--color-accent-500)]"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <MessageCircleQuestion className="w-4 h-4" />
      )}
    </button>
  );
}

/**
 * Кнопка с подписью, открывающая тот же чат. Для мест, где поддержка —
 * сам следующий шаг: «Написать нам» у тарифа без цены, «Обсудить
 * задачу» в разделе для бизнеса.
 *
 * Если чат не открылся, под кнопкой появляются Telegram и почта: на
 * странице, где это главное действие, молчащая кнопка значит потерянную
 * заявку.
 */
export function KnopkaChata({ children, className }: { children: ReactNode; className: string }) {
  const t = useTranslations('ais_layout');
  const { open, prigotovit, loading, sboj } = useChatPodderzhki();
  const pochta = pochtaPodderzhki(useLocale(), useAisRuDomen());
  const ssylka = 'underline underline-offset-4 hover:text-[var(--color-accent-400)]';
  return (
    <>
      <button
        type="button"
        onClick={open}
        onPointerEnter={prigotovit}
        onFocus={prigotovit}
        data-chat
        aria-busy={loading}
        className={className}
      >
        {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : children}
      </button>
      {sboj && (
        <p role="status" data-chat-zapasnoj className="mt-3 text-sm text-[var(--color-text-muted)]">
          {/* Текст из словаря с метками {bot} и {email}: ссылки
              вставляются на место меток, перевод остаётся цельной фразой. */}
          {t('chat_ne_otkrylsya', { bot: '\u0000bot\u0000', email: '\u0000email\u0000' })
            .split('\u0000')
            .map((kusok, i) =>
              kusok === 'bot' ? (
                <a
                  key={i}
                  href={AIS_RU_TELEGRAM_BOT.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ssylka}
                >
                  {AIS_RU_TELEGRAM_BOT.label}
                </a>
              ) : kusok === 'email' ? (
                <a key={i} href={'mailto:' + pochta} className={ssylka}>
                  {pochta}
                </a>
              ) : (
                kusok
              ),
            )}
        </p>
      )}
    </>
  );
}
